import type { AdminKey } from "./api";
import { adminKeyOptions } from "./logKeys";
import type { PickGroup } from "./search";
import { matchesTeam } from "./teamFilter";

/** "" is any division; UNLABELLED is keys with no division tag. */
export const UNLABELLED = "__unlabelled";
export const SPEND_FLOORS = [0, 1, 10, 50] as const;

export interface KeyFilter {
  /** From the Search picker: `person:<owner>` for one owner's keys, `key:<id>` for one key, "" for all. */
  pick: string;
  division: string;
  /** "" is any team; NO_TEAM is keys with none; otherwise a team id (teamFilter.ts). */
  team: string;
  minSpend: number;
  sort: "none" | "spend-desc" | "spend-asc";
}

/** How the Administration view names a key's owner. */
export function ownerLabel(k: AdminKey): string {
  if (k.ownerKind === "administrator") return "Corporate and AI (administrator)";
  if (k.ownerKind === "service") return `${k.serviceOwner ?? "No owner recorded"} (service key)`;
  if (k.ownerKind === "none") return k.teamId ? "No owner (team key)" : "No owner";
  return k.userEmail ?? k.userId ?? "";
}

/**
 * Who answers for a key, as one id: the person's user id, a service key's named owner, LiteLLM's
 * administrator placeholder; null for a key with no owner, which no person can be chosen to find.
 */
export function ownerOf(k: AdminKey): string | null {
  return k.ownerKind === "service" ? k.serviceOwner : k.ownerKind === "none" ? null : k.userId;
}

/**
 * The Search picker's choices: People (everyone who answers for a key, labelled as the Owner column
 * shows them, a service owner by email when one of their own keys gives it) then Keys.
 */
export function keySearchGroups(keys: AdminKey[]): PickGroup[] {
  const emails = new Map(keys.filter((k) => k.userId && k.userEmail).map((k) => [k.userId!, k.userEmail!]));
  const people = new Map<string, string>();
  for (const k of keys) {
    const id = ownerOf(k);
    if (!id || people.has(id)) continue;
    people.set(id, k.ownerKind === "administrator" ? ownerLabel(k) : emails.get(id) ?? id);
  }
  return [
    { text: "People", options: [...people].map(([id, label]) => ({ value: `person:${id}`, label })).sort((a, b) => a.label.localeCompare(b.label)) },
    { text: "Keys", options: adminKeyOptions(keys).map((o) => ({ ...o, value: `key:${o.value}` })) },
  ];
}

export function filterKeys(keys: AdminKey[], f: KeyFilter): AdminKey[] {
  const person = f.pick.startsWith("person:") ? f.pick.slice(7) : null;
  const id = f.pick.startsWith("key:") ? f.pick.slice(4) : null;
  const out = keys.filter((k) => {
    if (person !== null && ownerOf(k) !== person) return false;
    if (id !== null && k.id !== id) return false;
    if (f.division === UNLABELLED ? !!k.division : f.division && k.division !== f.division) return false;
    if (!matchesTeam(k, f.team)) return false;
    return k.spend >= f.minSpend;
  });
  if (f.sort !== "none") out.sort((a, b) => (f.sort === "spend-desc" ? b.spend - a.spend : a.spend - b.spend));
  return out;
}
